import type { LocalizedText } from "./Language";
import { outOfStockIngredients } from "./outOfStockIngredients";
import type { WorkshopLocation } from "./WorkshopLocation";
import { isPerfumeEmpty } from "./IsPerfumeEmpty";

export interface Perfume {
  recipeId: string;
  formulaNumber: string | null;
  created: Date;
  name: string;
  email: string;
  title: string;
  username: string;
  userLastName: string;
  components: PerfumeComponent[];
  workshopLocation: WorkshopLocation | null;
  comment: string;
  // set once the formula is finalized: only an admin can edit a locked perfume
  lockedAt: Date | null;
}

export function perfumeUserFullname(perfume: Perfume): string {
  return [perfume.title, perfume.username, perfume.userLastName].filter((c) => c !== "").join(" ");
}

export type PerfumeSummary = Pick<
  Perfume,
  "recipeId" | "formulaNumber" | "name" | "username" | "email" | "userLastName" | "created"
>;

// a perfume can be edited by its creator during this delay after its creation.
// Keep in sync with firestore.rules
export const PERFUME_EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;

export function isPerfumeEditable({
  perfume,
  now,
  isAdmin,
}: {
  perfume: Pick<Perfume, "created" | "lockedAt">;
  now: Date;
  isAdmin: boolean;
}): boolean {
  if (isAdmin) {
    return true;
  }
  return (
    perfume.lockedAt === null && now.getTime() - perfume.created.getTime() < PERFUME_EDIT_WINDOW_MS
  );
}

// code the store staff types to lock a formula by hand (Diploma step). Checked in the browser only:
// it keeps clients from locking their formula by mistake, it is not a security boundary
export const PERFUME_LOCK_CODE = "2165";

export function isPerfumeLockCode(code: string): boolean {
  return code.trim() === PERFUME_LOCK_CODE;
}

// an empty perfume older than this was prepared in advance: the client must create a new one
export const PERFUME_STALE_EMPTY_MS = 6 * 60 * 60 * 1000;

// `editStarted`: the client has started to edit this perfume on this page. Their input may not be
// saved yet (debounce, device waking up), so the saved perfume can still look empty: never stale
export function isPerfumeStaleEmpty({
  perfume,
  now,
  isAdmin,
  editStarted,
}: {
  perfume: Perfume;
  now: Date;
  isAdmin: boolean;
  editStarted: boolean;
}): boolean {
  return (
    !isAdmin &&
    !editStarted &&
    isPerfumeEmpty(perfume) &&
    now.getTime() - perfume.created.getTime() >= PERFUME_STALE_EMPTY_MS
  );
}

export enum ComponentCategory {
  NOTE_DE_TETE,
  NOTE_DE_COEUR,
  NOTE_DE_FOND,
}

export interface PerfumeIngredient {
  name: string;
  displayName: LocalizedText | null;
  description: LocalizedText | null;
  advice: string;
  galimardReference: string;
  category: ComponentCategory;
  icon: string;
}

export type CategoriesOfIngredients = Record<ComponentCategory, readonly PerfumeIngredient[]>;

export interface PerfumeComponent {
  name: string;
  volume: number;
  category: ComponentCategory;
  numeroDeLot: string | null;
  // added from "Suggest ingredients" (docs/ingredient-suggestion.md)
  suggested: boolean;
}

export function removeEmptyComponents(components: PerfumeComponent[]): PerfumeComponent[] {
  return components.filter((c) => !!c.name);
}

export function perfumeCountComponents(perfume: Perfume): number {
  return removeEmptyComponents(perfume.components).length;
}

export function perfumeVolume(perfume: Perfume): number {
  return removeEmptyComponents(perfume.components).reduce((acc, component) => {
    return acc + component.volume;
  }, 0);
}

export function makeFormulaNumber({
  formulaNumberPrefix,
  formulaNumber,
}: {
  formulaNumberPrefix: string;
  formulaNumber: number;
}): string {
  return `${formulaNumberPrefix}-${String(formulaNumber).padStart(5, "0")}`;
}

export const defaultPerfumeIngredientIcon = "/ingredients/unknown-ingredient";

export function newPerfumeIngredient({
  name,
  category,
}: {
  name: string;
  category: ComponentCategory;
}): PerfumeIngredient {
  return {
    name,
    displayName: null,
    description: null,
    advice: "",
    galimardReference: "",
    category,
    icon: defaultPerfumeIngredientIcon,
  };
}

export function sortIngredients(ingredients: PerfumeIngredient[]): PerfumeIngredient[] {
  return [...ingredients].sort((a, b) => a.name.localeCompare(b.name));
}

export function buildIngredients({
  ingredients,
  additionalIngredients,
}: {
  ingredients: CategoriesOfIngredients;
  additionalIngredients: PerfumeIngredient[];
}): CategoriesOfIngredients {
  // const result = { ...ingredients }

  const filterIngredients = (i: PerfumeIngredient) =>
    !outOfStockIngredients.includes(i.name.toUpperCase());

  // remove unavailable ingredients
  const result: CategoriesOfIngredients = {
    [ComponentCategory.NOTE_DE_TETE]:
      ingredients[ComponentCategory.NOTE_DE_TETE].filter(filterIngredients),
    [ComponentCategory.NOTE_DE_COEUR]:
      ingredients[ComponentCategory.NOTE_DE_COEUR].filter(filterIngredients),
    [ComponentCategory.NOTE_DE_FOND]:
      ingredients[ComponentCategory.NOTE_DE_FOND].filter(filterIngredients),
  };

  // add custom ingredients
  additionalIngredients.forEach((ingredientToAdd) => {
    const category = ingredientToAdd.category;
    // sort for each added ingredient (the number of ingredient to add is expected to be low)
    result[category] = sortIngredients([...result[category], ingredientToAdd]);
  });

  return result;
}

export function makeIngredient(
  component: PerfumeComponent,
  available: PerfumeIngredient[],
  option?: {
    createMissingIngredient: boolean;
  },
): [PerfumeIngredient | null, boolean] {
  if (!component.name) {
    return [null, false];
  }
  const componentName = component.name.toUpperCase();
  const ingredient =
    available.find((ingredient) => ingredient.name.toUpperCase() === componentName) ?? null;

  if (ingredient === null && option?.createMissingIngredient) {
    return [
      newPerfumeIngredient({
        name: component.name,
        category: component.category,
      }),
      true,
    ];
  }
  return [ingredient, false];
}
